import { useEffect, useRef, type ReactNode } from "react";

import { useRailPresence } from "@/hooks/useRailPresence";
import { cn } from "@/lib/utils";

/** Animated enter/exit for a rail card in desktop stack mode. */
export function RailStackCardMount({
  active,
  stack,
  grow,
  pinBottom,
  pinExpanded,
  className,
  children,
}: {
  active: boolean;
  stack: boolean;
  /** Card shares expanded height with other open stack cards. */
  grow?: boolean;
  /** Pinned to the bottom of the desktop stack (Notes). */
  pinBottom?: boolean;
  /** Pinned card body is open — shares height with the scrollable upper stack. */
  pinExpanded?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const show = useRailPresence(active);
  const contentRef = useRef<ReactNode>(null);
  if (active && children != null && children !== false) {
    contentRef.current = children;
  }

  useEffect(() => {
    if (!show) contentRef.current = null;
  }, [show]);

  if (!stack) {
    if (!active) return null;
    return <>{children}</>;
  }

  if (!show || contentRef.current == null) return null;

  return (
    <div
      className={cn(
        "rail-stack-card-mount",
        active && "rail-stack-card-mount--active",
        grow && active && "rail-stack-card-mount--grow",
        pinBottom && "rail-stack-card-mount--pin-bottom",
        pinBottom && pinExpanded && "rail-stack-card-mount--pin-expanded",
        className,
      )}
      aria-hidden={!active}
    >
      <div className="rail-stack-card-mount__inner">{contentRef.current}</div>
    </div>
  );
}
